export interface StateBenefit {
  id: string;
  stateId: string;
  stateName: string;
  title: string;
  category: 'education' | 'healthcare' | 'housing' | 'financial' | 'recreation' | 'employment' | 'burial' | 'other';
  description: string;
  eligibility: string[];
  applicationProcess: string;
  contactInfo: string;
  website: string;
  lastUpdated: string;
  specialNotes?: string;
  dependentEligibility?: string;
  ptBenefits?: string;
}

export const stateBenefitsData: StateBenefit[] = [
  // Florida Benefits
  {
    id: 'fl-edu-001',
    stateId: 'FL',
    stateName: 'Florida',
    title: 'Florida Education Tuition Waiver',
    category: 'education',
    description: 'Covers remaining cost of tuition after 9/11 GI Bill at Florida public colleges and universities. Dependents of 100% P&T veterans may also qualify for free education.',
    eligibility: [
      'Must be a Florida resident',
      'Veteran must have served during wartime period',
      'Must have been honorably discharged',
      'For dependents: Parent must be 100% permanent & total disabled',
      'Dependent must be under 26 years old',
      'Must maintain minimum 2.0 GPA'
    ],
    applicationProcess: 'Apply through your college financial aid office. Submit DD-214, proof of Florida residency, and disability rating letter if applicable.',
    contactInfo: 'Florida Department of Veterans Affairs: (850) 487-1533',
    website: 'https://floridavets.org/benefits-services/education/',
    lastUpdated: 'Jan 2026',
    specialNotes: 'This benefit covers the gap between federal 9/11 GI Bill benefits and actual tuition costs at Florida institutions.',
    dependentEligibility: 'Dependents of 100% P&T veterans qualify for full tuition waiver at Florida public institutions.',
    ptBenefits: '100% P&T veterans: Dependents receive free tuition at all Florida state colleges and universities'
  },
  {
    id: 'fl-health-001',
    stateId: 'FL',
    stateName: 'Florida',
    title: 'Florida State Veterans Homes',
    category: 'healthcare',
    description: 'Seven state-operated skilled nursing facilities providing long-term care, memory care, and domiciliary care for eligible veterans.',
    eligibility: [
      'Must be Florida resident for at least one year',
      'Must be veteran with honorable discharge',
      'Must require skilled nursing or domiciliary care',
      'Priority given to service-connected disabled veterans',
      'Income and asset screening may apply'
    ],
    applicationProcess: 'Contact specific veterans home directly for application and clinical assessment. Medical evaluation required.',
    contactInfo: 'Florida Department of Veterans Affairs: (727) 319-7440',
    website: 'https://floridavets.org/benefits-services/state-veterans-homes/',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Homes located in Pembroke Pines, Daytona Beach, Land O Lakes, Lake City, Port Charlotte, Panama City, and St. Augustine.'
  },
  {
    id: 'fl-housing-001',
    stateId: 'FL',
    stateName: 'Florida',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for service-connected disabled veterans and 100% P&T disabled veterans. Additional discounts available for totally disabled veterans.',
    eligibility: [
      '10% or higher service-connected disability: $5,000 exemption',
      '100% permanent and total disability: Full exemption on homestead property',
      'Surviving spouse: May retain exemption if not remarried',
      'Must own and occupy as primary residence'
    ],
    applicationProcess: 'Apply with county property appraiser office. Submit DD-214 and VA disability rating letter by March 1st for current tax year.',
    contactInfo: 'Contact your county property appraiser',
    website: 'https://floridarevenue.com/property/Pages/Exemptions.aspx',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Disabled veterans receiving federal housing grants may qualify for additional exemptions.',
    ptBenefits: '100% P&T veterans receive complete property tax exemption on homestead property'
  },
  {
    id: 'fl-recreation-001',
    stateId: 'FL',
    stateName: 'Florida',
    title: 'State Parks & Recreation Benefits',
    category: 'recreation',
    description: 'Free admission to Florida state parks for disabled veterans. Discounted hunting and fishing licenses for veterans and disabled veterans.',
    eligibility: [
      'Service-connected disabled veterans: Free state park admission',
      'All honorably discharged veterans: Discounted hunting/fishing licenses',
      'Must provide proof of disability rating or DD-214'
    ],
    applicationProcess: 'Present VA disability rating letter or DD-214 at state park entrance or when purchasing licenses.',
    contactInfo: 'Florida Fish and Wildlife Conservation Commission: (850) 488-4676',
    website: 'https://myfwc.com/license/recreational/military-gold-sportsman/',
    lastUpdated: 'Nov 2025',
    specialNotes: '100% service-connected disabled veterans receive free Gold Sportsman License covering all hunting and fishing.'
  },
  {
    id: 'fl-dmv-001',
    stateId: 'FL',
    stateName: 'Florida',
    title: 'DMV Benefits & License Plates',
    category: 'other',
    description: 'Free veteran license plates, disabled veteran parking permits, and no-fee ID cards for qualified veterans.',
    eligibility: [
      'Must be honorably discharged veteran',
      'Disabled parking: Must have qualifying disability',
      'Free ID: Must be 100% service-connected disabled'
    ],
    applicationProcess: 'Apply at local DMV office with DD-214 and disability rating letter if applicable.',
    contactInfo: 'Florida Department of Highway Safety: (850) 617-2000',
    website: 'https://www.flhsmv.gov/veteran-services/',
    lastUpdated: 'Jan 2026'
  },

  // California Benefits
  {
    id: 'ca-edu-001',
    stateId: 'CA',
    stateName: 'California',
    title: 'California College Fee Waiver',
    category: 'education',
    description: 'Waives tuition and fees at California State University, University of California, and California Community College campuses for eligible veterans and their dependents.',
    eligibility: [
      'Must be California resident',
      'Veteran must have served during wartime',
      'Must have been discharged under honorable conditions',
      'Dependent must be under 27 years old',
      'Must maintain satisfactory academic progress',
      'Family income must not exceed $14,000 annually'
    ],
    applicationProcess: 'Submit application through college financial aid office with DD-214, proof of California residency, and income verification.',
    contactInfo: 'California Department of Veterans Affairs: (916) 653-2573',
    website: 'https://www.calvet.ca.gov/education',
    lastUpdated: 'Dec 2025',
    dependentEligibility: 'Dependents of veterans with service-connected disabilities or who died from service-connected causes qualify.'
  },
  {
    id: 'ca-health-001',
    stateId: 'CA',
    stateName: 'California',
    title: 'Veterans Home of California',
    category: 'healthcare',
    description: 'Eight state-operated veterans homes providing residential care, skilled nursing, and memory care.',
    eligibility: [
      'Must be California resident for at least one year',
      'Must be veteran with honorable discharge',
      'Must require skilled nursing or residential care',
      'Income and asset limits may apply based on level of care'
    ],
    applicationProcess: 'Contact Veterans Home directly to schedule assessment. Complete application with medical documentation.',
    contactInfo: 'California Department of Veterans Affairs: (800) 952-5626',
    website: 'https://www.calvet.ca.gov/homes',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Homes located in Yountville, Barstow, Chula Vista, West Los Angeles, Lancaster, Ventura, Fresno, and Redding.'
  },
  {
    id: 'ca-housing-001',
    stateId: 'CA',
    stateName: 'California',
    title: 'Property Tax Exemption',
    category: 'housing',
    description: 'Property tax exemption up to $196,262 for disabled veterans or their unmarried surviving spouses.',
    eligibility: [
      'Must have service-connected disability rating from VA',
      'Must own and occupy property as primary residence',
      'Property must be located in California',
      'Annual household income limits apply',
      'Surviving spouses maintain exemption if not remarried'
    ],
    applicationProcess: 'File claim with county assessor office by February 15th with proof of disability rating and income.',
    contactInfo: 'California State Board of Equalization: (916) 274-3350',
    website: 'https://www.boe.ca.gov/proptaxes/vetexemption.htm',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Exemption amount varies based on disability percentage and household income.'
  },
  {
    id: 'ca-employment-001',
    stateId: 'CA',
    stateName: 'California',
    title: 'Veterans Employment Services',
    category: 'employment',
    description: 'Priority hiring for state civil service positions, job training programs, and employment assistance.',
    eligibility: [
      'Must be honorably discharged veteran',
      'Must be California resident',
      'Must meet minimum qualifications for position'
    ],
    applicationProcess: 'Indicate veteran status on state job applications. Attach DD-214 for verification.',
    contactInfo: 'California Department of Veterans Affairs: (916) 653-2573',
    website: 'https://www.calvet.ca.gov/employment',
    lastUpdated: 'Oct 2025',
    specialNotes: 'Veterans receive additional credit points in civil service examinations.'
  },

  // Texas Benefits
  {
    id: 'tx-edu-001',
    stateId: 'TX',
    stateName: 'Texas',
    title: 'Hazlewood Act - Education Benefits',
    category: 'education',
    description: 'Provides up to 150 credit hours of tuition exemption at Texas public colleges and universities for eligible veterans and their dependents.',
    eligibility: [
      'Must be Texas resident',
      'Veteran must have served at least 181 days of active duty',
      'Must have been honorably discharged',
      'Must have designated Texas as home of record or entered service in Texas',
      'Unused hours can be transferred to spouse or children'
    ],
    applicationProcess: 'Submit Hazlewood application to college registrar with DD-214 and proof of Texas residency at time of entry.',
    contactInfo: 'Texas Veterans Commission: (512) 463-5408',
    website: 'https://www.tvc.texas.gov/education/hazlewood-act/',
    lastUpdated: 'Dec 2025',
    dependentEligibility: 'Eligible dependents must use benefits within 15 years of veteran completing service or death.'
  },
  {
    id: 'tx-health-001',
    stateId: 'TX',
    stateName: 'Texas',
    title: 'Texas State Veterans Homes',
    category: 'healthcare',
    description: 'Network of skilled nursing facilities and domiciliary care homes for Texas veterans.',
    eligibility: [
      'Must be Texas resident',
      'Must be honorably discharged veteran',
      'Must require skilled nursing or domiciliary care',
      'Priority given to service-connected disabled veterans'
    ],
    applicationProcess: 'Contact Texas Veterans Commission to begin application process and schedule clinical assessment.',
    contactInfo: 'Texas Veterans Commission: (512) 463-5533',
    website: 'https://www.tvc.texas.gov/state-veterans-homes/',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Multiple locations across Texas including Temple, Big Spring, Bonham, El Paso, Floresville, and others.'
  },
  {
    id: 'tx-housing-001',
    stateId: 'TX',
    stateName: 'Texas',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans ranging from partial to full exemption based on disability rating.',
    eligibility: [
      '10-29% disability: $5,000 exemption',
      '30-49% disability: $7,500 exemption',
      '50-69% disability: $10,000 exemption',
      '70-100% disability: $12,000 exemption',
      '100% disability or individual unemployability: Full exemption',
      'Surviving spouse: May retain full exemption'
    ],
    applicationProcess: 'Apply with county appraisal district. Submit DD-214 and VA disability rating letter.',
    contactInfo: 'Contact your county appraisal district',
    website: 'https://comptroller.texas.gov/taxes/property-tax/exemptions/',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Age 65 or older veterans with 10% or higher disability receive additional homestead exemption.'
  },
  {
    id: 'tx-employment-001',
    stateId: 'TX',
    stateName: 'Texas',
    title: 'Veterans Employment Preference',
    category: 'employment',
    description: 'Hiring preference for veterans in state government positions and requires private employers to provide interview opportunities.',
    eligibility: [
      'Must be honorably discharged veteran',
      'Must meet minimum qualifications for position',
      'Preference applies to initial hiring decisions'
    ],
    applicationProcess: 'Indicate veteran status on job applications and provide DD-214 when requested.',
    contactInfo: 'Texas Veterans Commission: (512) 463-5533',
    website: 'https://www.tvc.texas.gov/employment/',
    lastUpdated: 'Oct 2025',
    specialNotes: 'Disabled veterans and Purple Heart recipients receive highest employment preference.'
  },

  // New York Benefits
  {
    id: 'ny-edu-001',
    stateId: 'NY',
    stateName: 'New York',
    title: 'Veterans Tuition Awards (VTA)',
    category: 'education',
    description: 'Provides full-time or part-time undergraduate tuition assistance for qualifying veterans at SUNY and CUNY institutions.',
    eligibility: [
      'Must be New York resident',
      'Must have served in hostilities after February 28, 1961',
      'Must have been honorably discharged',
      'Cannot be in default on federal student loans',
      'Must maintain satisfactory academic progress'
    ],
    applicationProcess: 'Apply through HESC website with DD-214 and proof of New York residency.',
    contactInfo: 'NYS Higher Education Services Corporation: (888) 697-4372',
    website: 'https://www.hesc.ny.gov/pay-for-college/financial-aid/types-of-financial-aid/nys-grants-scholarships-awards/veterans-tuition-awards.html',
    lastUpdated: 'Dec 2025',
    specialNotes: 'Award covers actual tuition charged up to SUNY/CUNY undergraduate rates.'
  },
  {
    id: 'ny-health-001',
    stateId: 'NY',
    stateName: 'New York',
    title: 'New York State Veterans Homes',
    category: 'healthcare',
    description: 'Network of skilled nursing facilities providing 24-hour care for eligible veterans.',
    eligibility: [
      'Must be New York resident',
      'Must be veteran with honorable discharge or active duty member',
      'Must require skilled nursing level of care',
      'Spouses may be eligible for admission'
    ],
    applicationProcess: 'Contact specific veterans home for application and clinical assessment scheduling.',
    contactInfo: 'New York State Division of Veterans Services: (518) 474-6114',
    website: 'https://veterans.ny.gov/content/new-york-state-veterans-homes',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Homes located in Batavia, Montrose, Oxford, St. Albans, and other locations statewide.'
  },
  {
    id: 'ny-housing-001',
    stateId: 'NY',
    stateName: 'New York',
    title: 'Alternative Veterans Exemption',
    category: 'housing',
    description: 'Property tax exemption for qualifying veterans, with increased exemptions for combat veterans and disabled veterans.',
    eligibility: [
      'Must have served during wartime',
      'Must have been honorably discharged',
      'Cold War veterans: 10% of assessed value',
      'Combat zone veterans: 15% of assessed value',
      'Disabled veterans: Additional 50% for 70%+ disability'
    ],
    applicationProcess: 'Apply with local assessor office. Submit DD-214 and disability rating if applicable.',
    contactInfo: 'Contact your local assessor',
    website: 'https://www.tax.ny.gov/pit/property/exemption/realpropertytaxexemptions.htm',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Gold Star parents and surviving spouses may also qualify for exemptions.'
  },

  // Pennsylvania Benefits
  {
    id: 'pa-edu-001',
    stateId: 'PA',
    stateName: 'Pennsylvania',
    title: 'Educational Gratuity Program',
    category: 'education',
    description: 'Free tuition at Pennsylvania state-owned institutions for children of veterans killed in service or who died of service-connected disabilities.',
    eligibility: [
      'Must be child of eligible veteran',
      'Veteran must have been Pennsylvania resident',
      'Must be between ages 16-23',
      'Must maintain satisfactory academic progress'
    ],
    applicationProcess: 'Submit application through PHEAA with death certificate and service records.',
    contactInfo: 'Pennsylvania Higher Education Assistance Agency: (800) 692-7392',
    website: 'https://www.pheaa.org/funding-opportunities/other-educational-aid/pa-state-grant-program/educational-gratuity.shtml',
    lastUpdated: 'Dec 2025',
    dependentEligibility: 'Children of 100% disabled veterans or former POWs may also qualify.'
  },
  {
    id: 'pa-health-001',
    stateId: 'PA',
    stateName: 'Pennsylvania',
    title: 'State Veterans Homes',
    category: 'healthcare',
    description: 'Six state-operated veterans homes providing skilled nursing and personal care services.',
    eligibility: [
      'Must be Pennsylvania resident',
      'Must be veteran with honorable discharge',
      'Must require nursing home level of care',
      'Priority to service-connected disabled veterans'
    ],
    applicationProcess: 'Contact specific veterans home for application and assessment.',
    contactInfo: 'Pennsylvania Department of Military and Veterans Affairs: (717) 861-8719',
    website: 'https://www.dmva.pa.gov/veteranshomes/Pages/default.aspx',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Homes located in Erie, Hollidaysburg, Philadelphia, Pittsburgh, Scranton, and Spring City.'
  },
  {
    id: 'pa-housing-001',
    stateId: 'PA',
    stateName: 'Pennsylvania',
    title: 'Real Estate Tax Exemption',
    category: 'housing',
    description: 'Property tax exemption for totally disabled veterans and their surviving spouses.',
    eligibility: [
      'Must have 100% service-connected disability',
      'Must own and occupy property as primary residence',
      'Surviving spouse maintains exemption while unmarried',
      'May not have annual income exceeding amount set by county'
    ],
    applicationProcess: 'Apply with county tax assessment office with VA disability rating letter.',
    contactInfo: 'Contact your county tax assessment office',
    website: 'https://www.dmva.pa.gov/veteransservices/Pages/Real-Estate-Tax-Exemption-Program.aspx',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Must reapply annually if income verification required by county.'
  },

  // Ohio Benefits
  {
    id: 'oh-edu-001',
    stateId: 'OH',
    stateName: 'Ohio',
    title: 'Ohio War Orphans & Severely Disabled Veterans Children Scholarship',
    category: 'education',
    description: 'Full tuition assistance at Ohio public colleges for children of deceased or severely disabled veterans.',
    eligibility: [
      'Must be child of eligible veteran',
      'Veteran must have been Ohio resident',
      'Must be age 16-25',
      'Parent must be 60%+ service-connected disabled or killed in action'
    ],
    applicationProcess: 'Apply through Ohio Department of Higher Education with parent veteran documentation.',
    contactInfo: 'Ohio Department of Higher Education: (614) 466-7420',
    website: 'https://highered.ohio.gov/students/paying/grants/owosd',
    lastUpdated: 'Dec 2025',
    dependentEligibility: 'Renewable for up to 5 years or until 26th birthday.'
  },
  {
    id: 'oh-health-001',
    stateId: 'OH',
    stateName: 'Ohio',
    title: 'Ohio Veterans Homes',
    category: 'healthcare',
    description: 'Three state-operated nursing homes providing skilled nursing care for eligible Ohio veterans.',
    eligibility: [
      'Must be Ohio resident for at least one year',
      'Must be veteran with honorable discharge',
      'Must require skilled nursing level of care'
    ],
    applicationProcess: 'Contact specific veterans home for application and clinical evaluation.',
    contactInfo: 'Ohio Department of Veterans Services: (614) 644-0898',
    website: 'https://dvs.ohio.gov/wps/portal/gov/dvs/veterans-benefits/ohio-veterans-homes',
    lastUpdated: 'Jan 2026',
    specialNotes: 'Homes located in Georgetown, Sandusky, and Springfield.'
  },
  {
    id: 'oh-housing-001',
    stateId: 'OH',
    stateName: 'Ohio',
    title: 'Homestead Exemption',
    category: 'housing',
    description: 'Property tax reduction for disabled veterans and surviving spouses.',
    eligibility: [
      'Must have 100% service-connected disability',
      'Must be rated as totally disabled by VA',
      'Must own and occupy property as primary residence',
      'Surviving spouse maintains benefit while unmarried'
    ],
    applicationProcess: 'File application with county auditor by December 31st with VA disability rating.',
    contactInfo: 'Contact your county auditor',
    website: 'https://tax.ohio.gov/individual/resources/exemptions',
    lastUpdated: 'Nov 2025',
    specialNotes: 'Exempts first $50,000 of market value from taxation.'
  },

  // Alabama Benefits
  {
    id: 'al-edu-001',
    stateId: 'AL',
    stateName: 'Alabama',
    title: 'Alabama GI Dependent Scholarship',
    category: 'education',
    description: 'Provides tuition assistance for dependents of veterans who were killed in action, died from service-connected causes, or are 100% P&T disabled.',
    eligibility: [
      'Parent must be Alabama veteran',
      'Dependent must be under 26 years old',
      'Must maintain 2.0 GPA',
      'Parent must be 100% P&T disabled, POW, or MIA'
    ],
    applicationProcess: 'Apply through Alabama Department of Veterans Affairs with parent veteran documentation.',
    contactInfo: 'Alabama Department of Veterans Affairs: (334) 242-5077',
    website: 'https://va.alabama.gov/gi-dependent-scholarship/',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Children and spouses of qualifying veterans receive free tuition at Alabama public institutions.',
    ptBenefits: '100% P&T veterans: Dependents receive full tuition waiver at Alabama state schools'
  },
  {
    id: 'al-housing-001',
    stateId: 'AL',
    stateName: 'Alabama',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans and surviving spouses.',
    eligibility: [
      '100% service-connected disabled: Full exemption on primary residence',
      'Surviving spouse: May retain exemption if not remarried'
    ],
    applicationProcess: 'Apply with county tax assessor with VA disability rating letter.',
    contactInfo: 'Contact your county tax assessor',
    website: 'https://va.alabama.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'al-health-001',
    stateId: 'AL',
    stateName: 'Alabama',
    title: 'Alabama State Veterans Homes',
    category: 'healthcare',
    description: 'State veterans homes providing skilled nursing care for eligible veterans.',
    eligibility: [
      'Must be Alabama resident',
      'Must be veteran with honorable discharge',
      'Must require skilled nursing care'
    ],
    applicationProcess: 'Contact Alabama Department of Veterans Affairs for application.',
    contactInfo: 'Alabama Department of Veterans Affairs: (334) 242-5077',
    website: 'https://va.alabama.gov/state-veterans-homes/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'al-dmv-001',
    stateId: 'AL',
    stateName: 'Alabama',
    title: 'Veteran License Designation & Plates',
    category: 'other',
    description: 'Veteran designation on driver licenses and specialized veteran license plates.',
    eligibility: [
      'Must be honorably discharged veteran',
      'Must provide DD-214'
    ],
    applicationProcess: 'Apply at Alabama DMV office with DD-214.',
    contactInfo: 'Alabama DMV: (334) 242-4400',
    website: 'https://www.alea.gov/dps/driver-license',
    lastUpdated: 'Dec 2025'
  },

  // Alaska Benefits
  {
    id: 'ak-housing-001',
    stateId: 'AK',
    stateName: 'Alaska',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans on primary residence.',
    eligibility: [
      '50% or higher service-connected disability',
      'Must own and occupy as primary residence',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with local assessor office with VA disability documentation.',
    contactInfo: 'Contact local assessor',
    website: 'http://veterans.alaska.gov/property-tax.html',
    lastUpdated: 'Jan 2026',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'ak-health-001',
    stateId: 'AK',
    stateName: 'Alaska',
    title: 'Alaska Veterans Homes',
    category: 'healthcare',
    description: 'State-operated veterans homes providing skilled nursing care.',
    eligibility: [
      'Must be Alaska resident',
      'Must be veteran with honorable discharge',
      'Must require skilled nursing care'
    ],
    applicationProcess: 'Contact Alaska Veterans Affairs for application.',
    contactInfo: 'Alaska Department of Military and Veterans Affairs: (907) 428-6016',
    website: 'http://veterans.alaska.gov/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ak-recreation-001',
    stateId: 'AK',
    stateName: 'Alaska',
    title: 'Recreation & Parks Benefits',
    category: 'recreation',
    description: 'Free admission to state veterans homes, reduced hunting/fishing licenses for disabled veterans.',
    eligibility: [
      'Must be Alaska resident veteran',
      'Disabled veterans: Additional discounts on licenses'
    ],
    applicationProcess: 'Present DD-214 or disability rating at parks or when purchasing licenses.',
    contactInfo: 'Alaska Department of Fish and Game: (907) 465-4100',
    website: 'http://www.adfg.alaska.gov/index.cfm?adfg=license.resident',
    lastUpdated: 'Nov 2025'
  },

  // Arizona Benefits
  {
    id: 'az-edu-001',
    stateId: 'AZ',
    stateName: 'Arizona',
    title: 'Tuition Waivers for Veterans & Dependents',
    category: 'education',
    description: 'Tuition waivers at Arizona public universities and community colleges for veterans and dependents of disabled or deceased veterans.',
    eligibility: [
      'Must be Arizona resident',
      'Veteran must have been honorably discharged',
      'Dependents: Parent must be 100% P&T disabled or killed in action'
    ],
    applicationProcess: 'Apply through college financial aid office with veteran documentation.',
    contactInfo: 'Arizona Department of Veterans Services: (602) 255-3373',
    website: 'https://dvs.az.gov/education',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T disabled veterans qualify for tuition waivers.',
    ptBenefits: '100% P&T veterans and dependents receive tuition waivers at Arizona public institutions'
  },
  {
    id: 'az-housing-001',
    stateId: 'AZ',
    stateName: 'Arizona',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans and surviving spouses.',
    eligibility: [
      '100% service-connected disabled: Full exemption',
      'Widows/widowers of military: Exemption available'
    ],
    applicationProcess: 'Apply with county assessor with VA disability rating.',
    contactInfo: 'Contact your county assessor',
    website: 'https://dvs.az.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'az-employment-001',
    stateId: 'AZ',
    stateName: 'Arizona',
    title: 'Veterans Employment Services',
    category: 'employment',
    description: 'Employment help, hiring preference, and job placement assistance for Arizona veterans.',
    eligibility: [
      'Must be honorably discharged veteran',
      'Arizona residency preferred'
    ],
    applicationProcess: 'Contact Arizona Department of Veterans Services.',
    contactInfo: 'Arizona Department of Veterans Services: (602) 255-3373',
    website: 'https://dvs.az.gov/employment',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'az-recreation-001',
    stateId: 'AZ',
    stateName: 'Arizona',
    title: 'Recreation Discounts',
    category: 'recreation',
    description: 'Reduced hunting/fishing licenses and park access for veterans.',
    eligibility: [
      'Must be Arizona resident veteran',
      'Disabled veterans: Enhanced discounts'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses or entering parks.',
    contactInfo: 'Arizona Game and Fish Department: (602) 942-3000',
    website: 'https://www.azgfd.com/license/',
    lastUpdated: 'Nov 2025'
  },

  // Arkansas Benefits
  {
    id: 'ar-edu-001',
    stateId: 'AR',
    stateName: 'Arkansas',
    title: 'Tuition Waivers for Dependents',
    category: 'education',
    description: 'Free or reduced tuition for dependents of veterans killed in action or 100% P&T disabled.',
    eligibility: [
      'Parent must be Arkansas veteran',
      'Parent must be 100% P&T disabled or killed in action',
      'Dependent must be under 23 years old'
    ],
    applicationProcess: 'Apply through Arkansas Department of Veterans Affairs.',
    contactInfo: 'Arkansas Department of Veterans Affairs: (501) 370-3820',
    website: 'https://www.veterans.arkansas.gov/education',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive tuition assistance.',
    ptBenefits: '100% P&T veterans: Dependents receive free tuition at Arkansas public schools'
  },
  {
    id: 'ar-housing-001',
    stateId: 'AR',
    stateName: 'Arkansas',
    title: 'Property Tax Relief',
    category: 'housing',
    description: 'Tax breaks on retirement income and property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption',
      'Surviving spouse: May retain exemption'
    ],
    applicationProcess: 'Apply with county tax collector with VA rating letter.',
    contactInfo: 'Contact your county tax collector',
    website: 'https://www.veterans.arkansas.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax exemptions'
  },
  {
    id: 'ar-health-001',
    stateId: 'AR',
    stateName: 'Arkansas',
    title: 'Veterans Homes & Healthcare',
    category: 'healthcare',
    description: 'State veterans homes providing skilled nursing and support services.',
    eligibility: [
      'Must be Arkansas resident',
      'Must be veteran with honorable discharge'
    ],
    applicationProcess: 'Contact Arkansas Department of Veterans Affairs.',
    contactInfo: 'Arkansas Department of Veterans Affairs: (501) 370-3820',
    website: 'https://www.veterans.arkansas.gov/homes',
    lastUpdated: 'Jan 2026'
  },

  // Colorado Benefits
  {
    id: 'co-housing-001',
    stateId: 'CO',
    stateName: 'Colorado',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Exemption on primary residence',
      'Must be Colorado resident'
    ],
    applicationProcess: 'Apply with county assessor with VA disability documentation.',
    contactInfo: 'Contact your county assessor',
    website: 'https://dmva.colorado.gov/veterans-benefits',
    lastUpdated: 'Jan 2026',
    ptBenefits: '100% P&T disabled veterans receive property tax exemption on primary residence'
  },
  {
    id: 'co-health-001',
    stateId: 'CO',
    stateName: 'Colorado',
    title: 'Colorado State Veterans Homes',
    category: 'healthcare',
    description: 'State veterans homes providing nursing care and assisted living.',
    eligibility: [
      'Must be Colorado resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Colorado Department of Military and Veterans Affairs.',
    contactInfo: 'Colorado DMVA: (303) 914-5835',
    website: 'https://dmva.colorado.gov/veterans-homes',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'co-employment-001',
    stateId: 'CO',
    stateName: 'Colorado',
    title: 'Veterans Employment Preference',
    category: 'employment',
    description: 'Hiring preference for state government positions.',
    eligibility: [
      'Must be honorably discharged veteran',
      'Must meet minimum job qualifications'
    ],
    applicationProcess: 'Indicate veteran status on state job applications.',
    contactInfo: 'Colorado DMVA: (303) 914-5835',
    website: 'https://dmva.colorado.gov/employment',
    lastUpdated: 'Dec 2025'
  },

  // Connecticut Benefits
  {
    id: 'ct-edu-001',
    stateId: 'CT',
    stateName: 'Connecticut',
    title: 'Education Assistance',
    category: 'education',
    description: 'Tuition waivers for veterans at Connecticut public colleges.',
    eligibility: [
      'Must be Connecticut resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through college financial aid office.',
    contactInfo: 'Connecticut Department of Veterans Affairs: (860) 616-3603',
    website: 'https://portal.ct.gov/DVA/Benefits/Education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ct-housing-001',
    stateId: 'CT',
    stateName: 'Connecticut',
    title: 'Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Tax exemption',
      'Must own primary residence in Connecticut'
    ],
    applicationProcess: 'Apply with local assessor with VA rating.',
    contactInfo: 'Contact local assessor',
    website: 'https://portal.ct.gov/DVA/Benefits/Tax-Relief',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax exemptions'
  },
  {
    id: 'ct-dmv-001',
    stateId: 'CT',
    stateName: 'Connecticut',
    title: 'License & DMV Perks',
    category: 'other',
    description: 'Veteran license plates and DMV fee waivers.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply at Connecticut DMV with DD-214.',
    contactInfo: 'Connecticut DMV: (860) 263-5700',
    website: 'https://portal.ct.gov/DMV',
    lastUpdated: 'Dec 2025'
  },

  // Delaware Benefits
  {
    id: 'de-edu-001',
    stateId: 'DE',
    stateName: 'Delaware',
    title: 'Education Support',
    category: 'education',
    description: 'Tuition assistance for Delaware veterans and dependents.',
    eligibility: [
      'Must be Delaware resident',
      'Must be honorably discharged veteran or dependent'
    ],
    applicationProcess: 'Apply through Delaware Commission of Veterans Affairs.',
    contactInfo: 'Delaware Commission of Veterans Affairs: (302) 739-2792',
    website: 'https://vets.delaware.gov/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'de-housing-001',
    stateId: 'DE',
    stateName: 'Delaware',
    title: 'Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax relief',
      'Must be Delaware resident'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://vets.delaware.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax exemptions'
  },
  {
    id: 'de-employment-001',
    stateId: 'DE',
    stateName: 'Delaware',
    title: 'Employment Preference',
    category: 'employment',
    description: 'State hiring preference for veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Delaware Veterans Affairs: (302) 739-2792',
    website: 'https://vets.delaware.gov/employment/',
    lastUpdated: 'Dec 2025'
  },

  // District of Columbia Benefits
  {
    id: 'dc-edu-001',
    stateId: 'DC',
    stateName: 'District of Columbia',
    title: 'Education Benefits',
    category: 'education',
    description: 'Tuition assistance for DC veterans at local institutions.',
    eligibility: [
      'Must be DC resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through DC Office of Veterans Affairs.',
    contactInfo: 'DC Office of Veterans Affairs: (202) 724-5454',
    website: 'https://ova.dc.gov/page/education-benefits',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'dc-housing-001',
    stateId: 'DC',
    stateName: 'District of Columbia',
    title: 'Tax Benefits',
    category: 'housing',
    description: 'Property tax relief for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption'
    ],
    applicationProcess: 'Apply with DC Office of Tax and Revenue.',
    contactInfo: 'DC OTR: (202) 727-4829',
    website: 'https://otr.cfo.dc.gov/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax relief'
  },
  {
    id: 'dc-employment-001',
    stateId: 'DC',
    stateName: 'District of Columbia',
    title: 'Employment Preference',
    category: 'employment',
    description: 'Hiring preference for DC government positions.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on applications.',
    contactInfo: 'DC OVA: (202) 724-5454',
    website: 'https://ova.dc.gov/',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'dc-dmv-001',
    stateId: 'DC',
    stateName: 'District of Columbia',
    title: 'DMV Perks',
    category: 'other',
    description: 'Veteran license plates and DMV fee discounts.',
    eligibility: [
      'Must be DC resident veteran'
    ],
    applicationProcess: 'Apply at DC DMV with DD-214.',
    contactInfo: 'DC DMV: (202) 737-4404',
    website: 'https://dmv.dc.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Georgia Benefits
  {
    id: 'ga-edu-001',
    stateId: 'GA',
    stateName: 'Georgia',
    title: 'Tuition Waivers',
    category: 'education',
    description: 'Tuition waivers at Georgia public institutions for veterans and dependents.',
    eligibility: [
      'Must be Georgia resident',
      'Veteran must have been honorably discharged',
      'Dependents of 100% P&T disabled veterans qualify'
    ],
    applicationProcess: 'Apply through college financial aid office.',
    contactInfo: 'Georgia Department of Veterans Service: (404) 656-2300',
    website: 'https://veterans.georgia.gov/education',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive tuition waivers.',
    ptBenefits: '100% P&T veterans and dependents receive free tuition at Georgia public schools'
  },
  {
    id: 'ga-housing-001',
    stateId: 'GA',
    stateName: 'Georgia',
    title: 'Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption'
    ],
    applicationProcess: 'Apply with county tax assessor.',
    contactInfo: 'Contact county tax assessor',
    website: 'https://veterans.georgia.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'ga-dmv-001',
    stateId: 'GA',
    stateName: 'Georgia',
    title: 'License Plate Benefits',
    category: 'other',
    description: 'Veteran and disabled veteran license plates.',
    eligibility: [
      'Must be Georgia resident veteran'
    ],
    applicationProcess: 'Apply at Georgia DMV with DD-214.',
    contactInfo: 'Georgia DDS: (678) 413-8400',
    website: 'https://dds.georgia.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Hawaii Benefits
  {
    id: 'hi-edu-001',
    stateId: 'HI',
    stateName: 'Hawaii',
    title: 'Education Support',
    category: 'education',
    description: 'Tuition waivers for Hawaii veterans at public institutions.',
    eligibility: [
      'Must be Hawaii resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through University of Hawaii financial aid.',
    contactInfo: 'Hawaii Office of Veterans Services: (808) 733-4295',
    website: 'http://dod.hawaii.gov/ovs/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'hi-housing-001',
    stateId: 'HI',
    stateName: 'Hawaii',
    title: 'Housing Assistance',
    category: 'housing',
    description: 'Property tax exemptions and housing assistance for veterans.',
    eligibility: [
      '100% service-connected disabled: Housing benefits'
    ],
    applicationProcess: 'Apply with county real property tax office.',
    contactInfo: 'Contact county tax office',
    website: 'http://dod.hawaii.gov/ovs/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive housing assistance and property tax relief'
  },
  {
    id: 'hi-recreation-001',
    stateId: 'HI',
    stateName: 'Hawaii',
    title: 'Fee Exemptions',
    category: 'recreation',
    description: 'Park fee exemptions and recreation discounts.',
    eligibility: [
      'Must be Hawaii resident veteran'
    ],
    applicationProcess: 'Present DD-214 at state parks.',
    contactInfo: 'Hawaii State Parks: (808) 587-0300',
    website: 'https://dlnr.hawaii.gov/dsp/',
    lastUpdated: 'Nov 2025'
  },

  // Idaho Benefits
  {
    id: 'id-housing-001',
    stateId: 'ID',
    stateName: 'Idaho',
    title: 'Property Tax Breaks',
    category: 'housing',
    description: 'Property tax reduction for disabled veterans.',
    eligibility: [
      '10% or higher service-connected disability',
      'Must be Idaho resident'
    ],
    applicationProcess: 'Apply with county assessor with VA rating.',
    contactInfo: 'Contact county assessor',
    website: 'https://veterans.idaho.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax reduction'
  },
  {
    id: 'id-recreation-001',
    stateId: 'ID',
    stateName: 'Idaho',
    title: 'Free Park Access & Tax Credits',
    category: 'recreation',
    description: 'Free access to Idaho state parks and hunting/fishing discounts.',
    eligibility: [
      'Must be Idaho resident veteran',
      'Disabled veterans: Enhanced benefits'
    ],
    applicationProcess: 'Present DD-214 at parks or when purchasing licenses.',
    contactInfo: 'Idaho Department of Fish and Game: (208) 334-3700',
    website: 'https://idfg.idaho.gov/',
    lastUpdated: 'Nov 2025'
  },
  {
    id: 'id-health-001',
    stateId: 'ID',
    stateName: 'Idaho',
    title: 'Idaho State Veterans Home',
    category: 'healthcare',
    description: 'State veterans home providing skilled nursing care.',
    eligibility: [
      'Must be Idaho resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Idaho Division of Veterans Services.',
    contactInfo: 'Idaho Division of Veterans Services: (208) 780-1300',
    website: 'https://veterans.idaho.gov/idaho-state-veterans-home/',
    lastUpdated: 'Jan 2026'
  },

  // Illinois Benefits
  {
    id: 'il-edu-001',
    stateId: 'IL',
    stateName: 'Illinois',
    title: 'Illinois Veteran Grant',
    category: 'education',
    description: 'Tuition and fee waivers at Illinois public universities for veterans. Dependents of 100% P&T disabled veterans may qualify for additional assistance.',
    eligibility: [
      'Must be Illinois resident at time of entry into service',
      'Must have served at least one year of active duty',
      'Must have been honorably discharged',
      'Dependents: Parent must be 100% P&T disabled for enhanced benefits'
    ],
    applicationProcess: 'Apply through Illinois Department of Veterans Affairs.',
    contactInfo: 'Illinois Department of Veterans Affairs: (217) 782-6641',
    website: 'https://www.illinois.gov/veterans/benefits/education.html',
    lastUpdated: 'Jan 2026',
    ptBenefits: '100% P&T veterans: Dependents may receive tuition assistance at Illinois public institutions'
  },
  {
    id: 'il-housing-001',
    stateId: 'IL',
    stateName: 'Illinois',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans and surviving spouses.',
    eligibility: [
      '30% or higher service-connected disability',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with county assessor with VA rating letter.',
    contactInfo: 'Contact county assessor',
    website: 'https://www.illinois.gov/veterans/benefits/financial.html',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'il-employment-001',
    stateId: 'IL',
    stateName: 'Illinois',
    title: 'Employment Assistance',
    category: 'employment',
    description: 'Job training and employment services for Illinois veterans.',
    eligibility: [
      'Must be Illinois resident veteran'
    ],
    applicationProcess: 'Contact Illinois Veterans Affairs.',
    contactInfo: 'Illinois DVA: (217) 782-6641',
    website: 'https://www.illinois.gov/veterans/benefits/employment.html',
    lastUpdated: 'Dec 2025'
  },

  // Indiana Benefits
  {
    id: 'in-edu-001',
    stateId: 'IN',
    stateName: 'Indiana',
    title: 'Free Tuition for Dependents',
    category: 'education',
    description: 'Free tuition at Indiana public colleges for dependents of disabled or deceased veterans.',
    eligibility: [
      'Parent must be 80% or higher service-connected disabled or killed in action',
      'Dependent must be under 26 years old'
    ],
    applicationProcess: 'Apply through Indiana Department of Veterans Affairs.',
    contactInfo: 'Indiana Department of Veterans Affairs: (317) 232-3910',
    website: 'https://www.in.gov/dva/veterans-benefits/education/',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive free tuition at Indiana public schools.',
    ptBenefits: '100% P&T veterans: Dependents receive free tuition at all Indiana state institutions'
  },
  {
    id: 'in-housing-001',
    stateId: 'IN',
    stateName: 'Indiana',
    title: 'Tax Exemptions',
    category: 'housing',
    description: 'Property tax deductions for disabled veterans.',
    eligibility: [
      '10% or higher service-connected disability',
      '100% P&T disabled: Enhanced deduction'
    ],
    applicationProcess: 'Apply with county auditor.',
    contactInfo: 'Contact county auditor',
    website: 'https://www.in.gov/dva/veterans-benefits/financial/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax deduction'
  },
  {
    id: 'in-dmv-001',
    stateId: 'IN',
    stateName: 'Indiana',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and free veteran designation on licenses.',
    eligibility: [
      'Must be Indiana resident veteran'
    ],
    applicationProcess: 'Apply at Indiana BMV with DD-214.',
    contactInfo: 'Indiana BMV: (888) 692-6841',
    website: 'https://www.in.gov/bmv/',
    lastUpdated: 'Dec 2025'
  },

  // Iowa Benefits
  {
    id: 'ia-edu-001',
    stateId: 'IA',
    stateName: 'Iowa',
    title: 'Tuition Assistance',
    category: 'education',
    description: 'Tuition grants for Iowa veterans attending public institutions.',
    eligibility: [
      'Must be Iowa resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Iowa College Aid.',
    contactInfo: 'Iowa Department of Veterans Affairs: (515) 242-5331',
    website: 'https://va.iowa.gov/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ia-housing-001',
    stateId: 'IA',
    stateName: 'Iowa',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax credits for disabled veterans.',
    eligibility: [
      '70% or higher service-connected disability',
      '100% P&T disabled: Enhanced credit'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://va.iowa.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax credit'
  },
  {
    id: 'ia-employment-001',
    stateId: 'IA',
    stateName: 'Iowa',
    title: 'Employment Help',
    category: 'employment',
    description: 'Job assistance and hiring preference for Iowa veterans.',
    eligibility: [
      'Must be Iowa resident veteran'
    ],
    applicationProcess: 'Contact Iowa Veterans Affairs.',
    contactInfo: 'Iowa DVA: (515) 242-5331',
    website: 'https://va.iowa.gov/employment',
    lastUpdated: 'Dec 2025'
  },

  // Kansas Benefits
  {
    id: 'ks-edu-001',
    stateId: 'KS',
    stateName: 'Kansas',
    title: 'Education Benefits',
    category: 'education',
    description: 'Tuition assistance for Kansas veterans and dependents.',
    eligibility: [
      'Must be Kansas resident',
      'Must be honorably discharged veteran or qualifying dependent'
    ],
    applicationProcess: 'Apply through Kansas Commission on Veterans Affairs.',
    contactInfo: 'Kansas Commission on Veterans Affairs: (785) 296-3976',
    website: 'https://kcva.ks.gov/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ks-housing-001',
    stateId: 'KS',
    stateName: 'Kansas',
    title: 'Property Tax Breaks',
    category: 'housing',
    description: 'Property tax refunds for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability'
    ],
    applicationProcess: 'Apply with county appraiser.',
    contactInfo: 'Contact county appraiser',
    website: 'https://kcva.ks.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax relief'
  },
  {
    id: 'ks-dmv-001',
    stateId: 'KS',
    stateName: 'Kansas',
    title: 'DMV Perks',
    category: 'other',
    description: 'Veteran license plates and fee waivers.',
    eligibility: [
      'Must be Kansas resident veteran'
    ],
    applicationProcess: 'Apply at Kansas DMV with DD-214.',
    contactInfo: 'Kansas DMV: (785) 296-3963',
    website: 'https://www.ksrevenue.gov/dmv.html',
    lastUpdated: 'Dec 2025'
  },

  // Kentucky Benefits
  {
    id: 'ky-edu-001',
    stateId: 'KY',
    stateName: 'Kentucky',
    title: 'Tuition Waivers',
    category: 'education',
    description: 'Tuition waivers at Kentucky public institutions for veterans and dependents.',
    eligibility: [
      'Must be Kentucky resident',
      'Dependents of 100% P&T disabled veterans qualify'
    ],
    applicationProcess: 'Apply through Kentucky Department of Veterans Affairs.',
    contactInfo: 'Kentucky Department of Veterans Affairs: (502) 564-9203',
    website: 'https://veterans.ky.gov/benefits/Pages/education.aspx',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive tuition waivers.',
    ptBenefits: '100% P&T veterans and dependents receive free tuition at Kentucky public schools'
  },
  {
    id: 'ky-housing-001',
    stateId: 'KY',
    stateName: 'Kentucky',
    title: 'Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption'
    ],
    applicationProcess: 'Apply with county property valuation administrator.',
    contactInfo: 'Contact county PVA',
    website: 'https://veterans.ky.gov/benefits/Pages/property-tax.aspx',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'ky-dmv-001',
    stateId: 'KY',
    stateName: 'Kentucky',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and disabled veteran parking.',
    eligibility: [
      'Must be Kentucky resident veteran'
    ],
    applicationProcess: 'Apply at Kentucky DMV with DD-214.',
    contactInfo: 'Kentucky DMV: (502) 564-1257',
    website: 'https://drive.ky.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Louisiana Benefits
  {
    id: 'la-edu-001',
    stateId: 'LA',
    stateName: 'Louisiana',
    title: 'Tuition Waivers',
    category: 'education',
    description: 'Tuition exemptions at Louisiana public colleges for veterans and dependents.',
    eligibility: [
      'Must be Louisiana resident',
      'Dependents of 100% service-connected disabled veterans qualify'
    ],
    applicationProcess: 'Apply through Louisiana Department of Veterans Affairs.',
    contactInfo: 'Louisiana Department of Veterans Affairs: (225) 922-0500',
    website: 'https://www.vetaffairs.la.gov/education',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive tuition waivers.',
    ptBenefits: '100% P&T veterans and dependents receive free tuition at Louisiana public schools'
  },
  {
    id: 'la-housing-001',
    stateId: 'LA',
    stateName: 'Louisiana',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with parish assessor.',
    contactInfo: 'Contact parish assessor',
    website: 'https://www.vetaffairs.la.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'la-employment-001',
    stateId: 'LA',
    stateName: 'Louisiana',
    title: 'Employment Preference',
    category: 'employment',
    description: 'State hiring preference for Louisiana veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Louisiana DVA: (225) 922-0500',
    website: 'https://www.vetaffairs.la.gov/employment',
    lastUpdated: 'Dec 2025'
  },

  // Maine Benefits
  {
    id: 'me-housing-001',
    stateId: 'ME',
    stateName: 'Maine',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for Maine veterans.',
    eligibility: [
      'Must be Maine resident veteran',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with local assessor.',
    contactInfo: 'Contact local assessor',
    website: 'https://www.maine.gov/veterans/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'me-recreation-001',
    stateId: 'ME',
    stateName: 'Maine',
    title: 'Parks & Outdoor Benefits',
    category: 'recreation',
    description: 'Free or discounted hunting/fishing licenses and park access.',
    eligibility: [
      'Must be Maine resident veteran',
      'Disabled veterans: Enhanced discounts'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses.',
    contactInfo: 'Maine Department of Inland Fisheries: (207) 287-8000',
    website: 'https://www.maine.gov/ifw/',
    lastUpdated: 'Nov 2025'
  },
  {
    id: 'me-health-001',
    stateId: 'ME',
    stateName: 'Maine',
    title: 'Maine Veterans Homes',
    category: 'healthcare',
    description: 'State veterans homes providing skilled nursing care.',
    eligibility: [
      'Must be Maine resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Maine Bureau of Veterans Services.',
    contactInfo: 'Maine Bureau of Veterans Services: (207) 430-6035',
    website: 'https://www.maine.gov/veterans/',
    lastUpdated: 'Jan 2026'
  },

  // Maryland Benefits
  {
    id: 'md-edu-001',
    stateId: 'MD',
    stateName: 'Maryland',
    title: 'Veterans Education Programs',
    category: 'education',
    description: 'Tuition waivers for Maryland veterans and dependents at state institutions.',
    eligibility: [
      'Must be Maryland resident',
      'Must be honorably discharged veteran or qualifying dependent'
    ],
    applicationProcess: 'Apply through Maryland Higher Education Commission.',
    contactInfo: 'Maryland Department of Veterans Affairs: (410) 260-3838',
    website: 'https://veterans.maryland.gov/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'md-housing-001',
    stateId: 'MD',
    stateName: 'Maryland',
    title: 'Property Tax Credits',
    category: 'housing',
    description: 'Property tax credits for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability',
      '100% P&T disabled: Enhanced credit'
    ],
    applicationProcess: 'Apply with State Department of Assessments and Taxation.',
    contactInfo: 'Maryland SDAT: (410) 767-1184',
    website: 'https://www.dat.maryland.gov/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax credit'
  },
  {
    id: 'md-employment-001',
    stateId: 'MD',
    stateName: 'Maryland',
    title: 'Hiring Preference',
    category: 'employment',
    description: 'State employment preference for Maryland veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state job applications.',
    contactInfo: 'Maryland DVA: (410) 260-3838',
    website: 'https://veterans.maryland.gov/employment/',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'md-dmv-001',
    stateId: 'MD',
    stateName: 'Maryland',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and fee waivers.',
    eligibility: [
      'Must be Maryland resident veteran'
    ],
    applicationProcess: 'Apply at Maryland MVA with DD-214.',
    contactInfo: 'Maryland MVA: (410) 768-7000',
    website: 'https://mva.maryland.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Massachusetts Benefits
  {
    id: 'ma-edu-001',
    stateId: 'MA',
    stateName: 'Massachusetts',
    title: 'Massachusetts Public Service Grant',
    category: 'education',
    description: 'Tuition waivers for Massachusetts veterans at state institutions.',
    eligibility: [
      'Must be Massachusetts resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Massachusetts Department of Veterans Services.',
    contactInfo: 'Massachusetts DVS: (617) 210-5480',
    website: 'https://www.mass.gov/orgs/massachusetts-department-of-veterans-services',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ma-housing-001',
    stateId: 'MA',
    stateName: 'Massachusetts',
    title: 'Homestead Tax Relief',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '10% or higher service-connected disability',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with local assessor.',
    contactInfo: 'Contact local assessor',
    website: 'https://www.mass.gov/veterans-benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'ma-employment-001',
    stateId: 'MA',
    stateName: 'Massachusetts',
    title: 'Hiring Preference',
    category: 'employment',
    description: 'State hiring preference for Massachusetts veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Massachusetts DVS: (617) 210-5480',
    website: 'https://www.mass.gov/veterans-employment',
    lastUpdated: 'Dec 2025'
  },

  // Michigan Benefits
  {
    id: 'mi-edu-001',
    stateId: 'MI',
    stateName: 'Michigan',
    title: 'Michigan Veteran Education Assistance',
    category: 'education',
    description: 'Tuition assistance for Michigan veterans attending state institutions.',
    eligibility: [
      'Must be Michigan resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Michigan Veterans Affairs Agency.',
    contactInfo: 'Michigan Veterans Affairs Agency: (517) 284-5300',
    website: 'https://www.michigan.gov/dmva/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'mi-housing-001',
    stateId: 'MI',
    stateName: 'Michigan',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Full exemption'
    ],
    applicationProcess: 'Apply with local assessor.',
    contactInfo: 'Contact local assessor',
    website: 'https://www.michigan.gov/dmva/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'mi-dmv-001',
    stateId: 'MI',
    stateName: 'Michigan',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and disabled veteran parking.',
    eligibility: [
      'Must be Michigan resident veteran'
    ],
    applicationProcess: 'Apply at Michigan Secretary of State with DD-214.',
    contactInfo: 'Michigan SOS: (888) 767-6424',
    website: 'https://www.michigan.gov/sos',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'mi-education-001',
    stateId: 'MI',
    stateName: 'Michigan',
    title: 'Educational Support',
    category: 'education',
    description: 'Tuition waivers and educational assistance for Michigan veterans.',
    eligibility: [
      'Must be Michigan resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Michigan DMVA.',
    contactInfo: 'Michigan DMVA: (517) 284-5300',
    website: 'https://www.michigan.gov/dmva/education',
    lastUpdated: 'Jan 2026'
  },

  // Minnesota Benefits
  {
    id: 'mn-edu-001',
    stateId: 'MN',
    stateName: 'Minnesota',
    title: 'Minnesota GI Bill',
    category: 'education',
    description: 'Education benefits for Minnesota veterans at state institutions.',
    eligibility: [
      'Must be Minnesota resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Minnesota Department of Veterans Affairs.',
    contactInfo: 'Minnesota Department of Veterans Affairs: (651) 296-2562',
    website: 'https://mn.gov/mdva/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'mn-housing-001',
    stateId: 'MN',
    stateName: 'Minnesota',
    title: 'Property Tax Exclusions',
    category: 'housing',
    description: 'Property tax exclusions for disabled veterans.',
    eligibility: [
      '70% or higher service-connected disability',
      '100% P&T disabled: Enhanced exclusion'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://mn.gov/mdva/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exclusion'
  },
  {
    id: 'mn-employment-001',
    stateId: 'MN',
    stateName: 'Minnesota',
    title: 'Employment Preference',
    category: 'employment',
    description: 'State hiring preference for Minnesota veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Minnesota MDVA: (651) 296-2562',
    website: 'https://mn.gov/mdva/employment/',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'mn-recreation-001',
    stateId: 'MN',
    stateName: 'Minnesota',
    title: 'Park Benefits',
    category: 'recreation',
    description: 'State park discounts and hunting/fishing license reductions.',
    eligibility: [
      'Must be Minnesota resident veteran'
    ],
    applicationProcess: 'Present DD-214 at parks or when purchasing licenses.',
    contactInfo: 'Minnesota DNR: (651) 296-6157',
    website: 'https://www.dnr.state.mn.us/',
    lastUpdated: 'Nov 2025'
  },

  // Mississippi Benefits
  {
    id: 'ms-edu-001',
    stateId: 'MS',
    stateName: 'Mississippi',
    title: 'Tuition Support',
    category: 'education',
    description: 'Tuition assistance for Mississippi veterans and dependents.',
    eligibility: [
      'Must be Mississippi resident',
      'Must be honorably discharged veteran or qualifying dependent'
    ],
    applicationProcess: 'Apply through Mississippi Veterans Affairs Board.',
    contactInfo: 'Mississippi Veterans Affairs Board: (601) 576-4850',
    website: 'https://www.msva.ms.gov/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ms-housing-001',
    stateId: 'MS',
    stateName: 'Mississippi',
    title: 'Tax Perks',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption'
    ],
    applicationProcess: 'Apply with county tax assessor.',
    contactInfo: 'Contact county tax assessor',
    website: 'https://www.msva.ms.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'ms-dmv-001',
    stateId: 'MS',
    stateName: 'Mississippi',
    title: 'License Benefits',
    category: 'other',
    description: 'Veteran license plates and fee discounts.',
    eligibility: [
      'Must be Mississippi resident veteran'
    ],
    applicationProcess: 'Apply at Mississippi DMV with DD-214.',
    contactInfo: 'Mississippi DMV: (601) 923-7670',
    website: 'https://www.driverservicebureau.dps.ms.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Missouri Benefits
  {
    id: 'mo-edu-001',
    stateId: 'MO',
    stateName: 'Missouri',
    title: 'Missouri Returning Heroes Education Act',
    category: 'education',
    description: 'Tuition assistance for Missouri combat veterans.',
    eligibility: [
      'Must be Missouri resident',
      'Must have served in combat zone after 9/11'
    ],
    applicationProcess: 'Apply through Missouri Department of Higher Education.',
    contactInfo: 'Missouri Veterans Commission: (573) 751-3779',
    website: 'https://mvc.dps.mo.gov/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'mo-housing-001',
    stateId: 'MO',
    stateName: 'Missouri',
    title: 'Property Tax Credits',
    category: 'housing',
    description: 'Property tax credits for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax credit'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://mvc.dps.mo.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax credit'
  },
  {
    id: 'mo-dmv-001',
    stateId: 'MO',
    stateName: 'Missouri',
    title: 'License Plate Benefits',
    category: 'other',
    description: 'Veteran and disabled veteran license plates.',
    eligibility: [
      'Must be Missouri resident veteran'
    ],
    applicationProcess: 'Apply at Missouri Department of Revenue with DD-214.',
    contactInfo: 'Missouri DOR: (573) 751-4600',
    website: 'https://dor.mo.gov/drivers/',
    lastUpdated: 'Dec 2025'
  },

  // Montana Benefits
  {
    id: 'mt-housing-001',
    stateId: 'MT',
    stateName: 'Montana',
    title: 'Property Tax Relief',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability',
      '100% P&T disabled: Enhanced relief'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://dma.mt.gov/Veterans/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax relief'
  },
  {
    id: 'mt-recreation-001',
    stateId: 'MT',
    stateName: 'Montana',
    title: 'Recreation Benefits',
    category: 'recreation',
    description: 'Free or discounted hunting/fishing licenses and park access.',
    eligibility: [
      'Must be Montana resident veteran',
      'Disabled veterans: Enhanced discounts'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses.',
    contactInfo: 'Montana Fish, Wildlife & Parks: (406) 444-2535',
    website: 'https://fwp.mt.gov/',
    lastUpdated: 'Nov 2025'
  },
  {
    id: 'mt-health-001',
    stateId: 'MT',
    stateName: 'Montana',
    title: 'Montana Veterans Homes',
    category: 'healthcare',
    description: 'State veterans homes providing skilled nursing care.',
    eligibility: [
      'Must be Montana resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Montana Department of Military Affairs.',
    contactInfo: 'Montana DMA: (406) 324-3742',
    website: 'https://dma.mt.gov/Veterans/',
    lastUpdated: 'Jan 2026'
  },

  // Nebraska Benefits
  {
    id: 'ne-edu-001',
    stateId: 'NE',
    stateName: 'Nebraska',
    title: 'Veterans Education Benefits',
    category: 'education',
    description: 'Tuition waivers for Nebraska veterans at state institutions.',
    eligibility: [
      'Must be Nebraska resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Nebraska Department of Veterans Affairs.',
    contactInfo: 'Nebraska Department of Veterans Affairs: (402) 420-4021',
    website: 'https://veterans.nebraska.gov/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ne-housing-001',
    stateId: 'NE',
    stateName: 'Nebraska',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://veterans.nebraska.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'ne-dmv-001',
    stateId: 'NE',
    stateName: 'Nebraska',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and disabled veteran parking.',
    eligibility: [
      'Must be Nebraska resident veteran'
    ],
    applicationProcess: 'Apply at Nebraska DMV with DD-214.',
    contactInfo: 'Nebraska DMV: (402) 471-3918',
    website: 'https://dmv.nebraska.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Nevada Benefits
  {
    id: 'nv-housing-001',
    stateId: 'NV',
    stateName: 'Nevada',
    title: 'Property Tax Deductions',
    category: 'housing',
    description: 'Property tax deductions for disabled veterans. No state income tax on disability or retirement pay.',
    eligibility: [
      '60% or higher service-connected disability',
      '100% P&T disabled: Enhanced deduction'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://veterans.nv.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax deduction and no state income tax'
  },
  {
    id: 'nv-recreation-001',
    stateId: 'NV',
    stateName: 'Nevada',
    title: 'Recreation Discounts',
    category: 'recreation',
    description: 'Discounted hunting/fishing licenses and park access.',
    eligibility: [
      'Must be Nevada resident veteran'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses.',
    contactInfo: 'Nevada Department of Wildlife: (775) 688-1500',
    website: 'https://www.ndow.org/',
    lastUpdated: 'Nov 2025'
  },
  {
    id: 'nv-health-001',
    stateId: 'NV',
    stateName: 'Nevada',
    title: 'Nevada State Veterans Home',
    category: 'healthcare',
    description: 'State veterans home providing skilled nursing care.',
    eligibility: [
      'Must be Nevada resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Nevada Department of Veterans Services.',
    contactInfo: 'Nevada DVS: (775) 688-1653',
    website: 'https://veterans.nv.gov/home/',
    lastUpdated: 'Jan 2026'
  },

  // New Hampshire Benefits
  {
    id: 'nh-housing-001',
    stateId: 'NH',
    stateName: 'New Hampshire',
    title: 'Property Tax Credits',
    category: 'housing',
    description: 'Property tax credits for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax credit'
    ],
    applicationProcess: 'Apply with local assessor.',
    contactInfo: 'Contact local assessor',
    website: 'https://www.dmavs.nh.gov/veterans-services',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax credit'
  },
  {
    id: 'nh-dmv-001',
    stateId: 'NH',
    stateName: 'New Hampshire',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and fee waivers.',
    eligibility: [
      'Must be New Hampshire resident veteran'
    ],
    applicationProcess: 'Apply at NH DMV with DD-214.',
    contactInfo: 'New Hampshire DMV: (603) 227-4000',
    website: 'https://www.nh.gov/safety/divisions/dmv/',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'nh-health-001',
    stateId: 'NH',
    stateName: 'New Hampshire',
    title: 'New Hampshire Veterans Home',
    category: 'healthcare',
    description: 'State veterans home providing skilled nursing care.',
    eligibility: [
      'Must be New Hampshire resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact NH Division of Military Affairs and Veterans Services.',
    contactInfo: 'NH DMAVS: (603) 624-9230',
    website: 'https://www.dmavs.nh.gov/',
    lastUpdated: 'Jan 2026'
  },

  // New Jersey Benefits
  {
    id: 'nj-edu-001',
    stateId: 'NJ',
    stateName: 'New Jersey',
    title: 'Veterans Tuition Credit Program',
    category: 'education',
    description: 'Tuition credits for New Jersey veterans at state institutions.',
    eligibility: [
      'Must be New Jersey resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through New Jersey Department of Military and Veterans Affairs.',
    contactInfo: 'New Jersey DMAVA: (609) 530-6854',
    website: 'https://www.nj.gov/military/veterans/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'nj-housing-001',
    stateId: 'NJ',
    stateName: 'New Jersey',
    title: 'Property Tax Deductions',
    category: 'housing',
    description: 'Property tax deductions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: $250 deduction',
      'Surviving spouse: May retain deduction'
    ],
    applicationProcess: 'Apply with local tax assessor.',
    contactInfo: 'Contact local assessor',
    website: 'https://www.nj.gov/military/veterans/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax deduction'
  },
  {
    id: 'nj-employment-001',
    stateId: 'NJ',
    stateName: 'New Jersey',
    title: 'Veterans Employment Services',
    category: 'employment',
    description: 'State hiring preference and job assistance for New Jersey veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact New Jersey DMAVA.',
    contactInfo: 'New Jersey DMAVA: (609) 530-6854',
    website: 'https://www.nj.gov/military/veterans/employment/',
    lastUpdated: 'Dec 2025'
  },

  // New Mexico Benefits
  {
    id: 'nm-edu-001',
    stateId: 'NM',
    stateName: 'New Mexico',
    title: 'Veterans Education Benefits',
    category: 'education',
    description: 'Tuition waivers for New Mexico veterans at state institutions.',
    eligibility: [
      'Must be New Mexico resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through New Mexico Department of Veterans Services.',
    contactInfo: 'New Mexico DVS: (505) 383-2400',
    website: 'http://www.nmdvs.org/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'nm-housing-001',
    stateId: 'NM',
    stateName: 'New Mexico',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'http://www.nmdvs.org/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'nm-recreation-001',
    stateId: 'NM',
    stateName: 'New Mexico',
    title: 'Parks & Recreation',
    category: 'recreation',
    description: 'Free pass to parks and monuments for disabled veterans.',
    eligibility: [
      'Must be New Mexico resident veteran',
      'Disabled veterans: Free park access'
    ],
    applicationProcess: 'Present disability rating at parks.',
    contactInfo: 'New Mexico State Parks: (505) 476-3355',
    website: 'https://www.emnrd.nm.gov/spd/',
    lastUpdated: 'Nov 2025'
  },

  // North Carolina Benefits
  {
    id: 'nc-edu-001',
    stateId: 'NC',
    stateName: 'North Carolina',
    title: 'NC Veterans Scholarship Program',
    category: 'education',
    description: 'Scholarships for children of disabled or deceased veterans.',
    eligibility: [
      'Parent must be North Carolina veteran',
      'Parent must be 20% or higher service-connected disabled or killed in action'
    ],
    applicationProcess: 'Apply through North Carolina Division of Veterans Affairs.',
    contactInfo: 'North Carolina DVA: (919) 807-4250',
    website: 'https://www.milvets.nc.gov/services/scholarship',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Children of disabled or deceased veterans qualify for scholarships.'
  },
  {
    id: 'nc-housing-001',
    stateId: 'NC',
    stateName: 'North Carolina',
    title: 'Property Tax Relief',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exclusion'
    ],
    applicationProcess: 'Apply with county tax assessor.',
    contactInfo: 'Contact county tax assessor',
    website: 'https://www.milvets.nc.gov/services/state-benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax exclusion'
  },
  {
    id: 'nc-dmv-001',
    stateId: 'NC',
    stateName: 'North Carolina',
    title: 'Adaptive Vehicle Tax Benefits & DMV Perks',
    category: 'other',
    description: 'Adaptive vehicle tax benefits and veteran license plates.',
    eligibility: [
      'Must be North Carolina resident veteran'
    ],
    applicationProcess: 'Apply at NC DMV with DD-214.',
    contactInfo: 'North Carolina DMV: (919) 715-7000',
    website: 'https://www.ncdot.gov/dmv/',
    lastUpdated: 'Dec 2025'
  },

  // North Dakota Benefits
  {
    id: 'nd-edu-001',
    stateId: 'ND',
    stateName: 'North Dakota',
    title: 'Free Tuition for Dependents',
    category: 'education',
    description: 'Free tuition at North Dakota public colleges for dependents of disabled or deceased veterans.',
    eligibility: [
      'Parent must be North Dakota veteran',
      'Parent must be 100% P&T disabled or killed in action'
    ],
    applicationProcess: 'Apply through North Dakota Department of Veterans Affairs.',
    contactInfo: 'North Dakota DVA: (701) 239-7165',
    website: 'http://www.nd.gov/veterans/benefits/education',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive free tuition at North Dakota public institutions.',
    ptBenefits: '100% P&T veterans: Dependents receive free tuition at North Dakota state schools'
  },
  {
    id: 'nd-housing-001',
    stateId: 'ND',
    stateName: 'North Dakota',
    title: 'Disability Property Tax Credits',
    category: 'housing',
    description: 'Property tax credits for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability',
      '100% P&T disabled: Enhanced credit'
    ],
    applicationProcess: 'Apply with county director of tax equalization.',
    contactInfo: 'Contact county tax office',
    website: 'http://www.nd.gov/veterans/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax credit'
  },
  {
    id: 'nd-dmv-001',
    stateId: 'ND',
    stateName: 'North Dakota',
    title: 'License Plate Perks',
    category: 'other',
    description: 'Veteran license plates and disabled veteran parking.',
    eligibility: [
      'Must be North Dakota resident veteran'
    ],
    applicationProcess: 'Apply at North Dakota DOT with DD-214.',
    contactInfo: 'North Dakota DOT: (701) 328-2725',
    website: 'https://www.dot.nd.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Oklahoma Benefits
  {
    id: 'ok-housing-001',
    stateId: 'OK',
    stateName: 'Oklahoma',
    title: 'Property Tax Relief',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Full exemption on homestead'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://oklahoma.gov/veterans/benefits.html',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'ok-dmv-001',
    stateId: 'OK',
    stateName: 'Oklahoma',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and disabled veteran parking.',
    eligibility: [
      'Must be Oklahoma resident veteran'
    ],
    applicationProcess: 'Apply at Oklahoma Service Oklahoma with DD-214.',
    contactInfo: 'Service Oklahoma: (405) 521-3221',
    website: 'https://oklahoma.gov/service/',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'ok-health-001',
    stateId: 'OK',
    stateName: 'Oklahoma',
    title: 'Oklahoma Veterans Centers',
    category: 'healthcare',
    description: 'State veterans centers providing skilled nursing care.',
    eligibility: [
      'Must be Oklahoma resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Oklahoma Department of Veterans Affairs.',
    contactInfo: 'Oklahoma DVA: (405) 521-3684',
    website: 'https://oklahoma.gov/veterans.html',
    lastUpdated: 'Jan 2026'
  },

  // Oregon Benefits
  {
    id: 'or-edu-001',
    stateId: 'OR',
    stateName: 'Oregon',
    title: 'Oregon Veterans Education Benefits',
    category: 'education',
    description: 'Tuition and fee waivers for Oregon veterans.',
    eligibility: [
      'Must be Oregon resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Oregon Department of Veterans Affairs.',
    contactInfo: 'Oregon Department of Veterans Affairs: (503) 373-2085',
    website: 'https://www.oregon.gov/odva/benefits/pages/education.aspx',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'or-housing-001',
    stateId: 'OR',
    stateName: 'Oregon',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '40% or higher service-connected disability',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://www.oregon.gov/odva/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'or-recreation-001',
    stateId: 'OR',
    stateName: 'Oregon',
    title: 'Recreation Access',
    category: 'recreation',
    description: 'Discounted hunting/fishing licenses and park access.',
    eligibility: [
      'Must be Oregon resident veteran'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses.',
    contactInfo: 'Oregon Department of Fish and Wildlife: (503) 947-6000',
    website: 'https://www.dfw.state.or.us/',
    lastUpdated: 'Nov 2025'
  },
  {
    id: 'or-dmv-001',
    stateId: 'OR',
    stateName: 'Oregon',
    title: 'License Benefits',
    category: 'other',
    description: 'Veteran license plates and fee waivers.',
    eligibility: [
      'Must be Oregon resident veteran'
    ],
    applicationProcess: 'Apply at Oregon DMV with DD-214.',
    contactInfo: 'Oregon DMV: (503) 945-5000',
    website: 'https://www.oregon.gov/odot/dmv/',
    lastUpdated: 'Dec 2025'
  },

  // Rhode Island Benefits
  {
    id: 'ri-edu-001',
    stateId: 'RI',
    stateName: 'Rhode Island',
    title: 'Education Assistance',
    category: 'education',
    description: 'Tuition waivers for Rhode Island veterans at state institutions.',
    eligibility: [
      'Must be Rhode Island resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Rhode Island Office of Veterans Services.',
    contactInfo: 'Rhode Island Office of Veterans Services: (401) 921-2119',
    website: 'https://vets.ri.gov/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ri-housing-001',
    stateId: 'RI',
    stateName: 'Rhode Island',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '10% or higher service-connected disability',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with local tax assessor.',
    contactInfo: 'Contact local assessor',
    website: 'https://vets.ri.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'ri-employment-001',
    stateId: 'RI',
    stateName: 'Rhode Island',
    title: 'Employment Preference',
    category: 'employment',
    description: 'State hiring preference for Rhode Island veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Rhode Island OVS: (401) 921-2119',
    website: 'https://vets.ri.gov/employment/',
    lastUpdated: 'Dec 2025'
  },

  // South Carolina Benefits
  {
    id: 'sc-edu-001',
    stateId: 'SC',
    stateName: 'South Carolina',
    title: 'Free Tuition Program',
    category: 'education',
    description: 'Free tuition for children of veterans killed in action or 100% P&T disabled.',
    eligibility: [
      'Parent must be South Carolina veteran',
      'Parent must be 100% P&T disabled or killed in action'
    ],
    applicationProcess: 'Apply through South Carolina Division of Veterans Affairs.',
    contactInfo: 'South Carolina DVA: (803) 647-2434',
    website: 'https://vets.sc.gov/education',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Children of 100% P&T veterans receive free tuition at South Carolina public schools.',
    ptBenefits: '100% P&T veterans: Dependents receive free tuition at South Carolina state institutions'
  },
  {
    id: 'sc-housing-001',
    stateId: 'SC',
    stateName: 'South Carolina',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability',
      '100% P&T disabled: Full exemption'
    ],
    applicationProcess: 'Apply with county auditor.',
    contactInfo: 'Contact county auditor',
    website: 'https://vets.sc.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'sc-dmv-001',
    stateId: 'SC',
    stateName: 'South Carolina',
    title: 'License Perks',
    category: 'other',
    description: 'Veteran license plates and disabled veteran parking.',
    eligibility: [
      'Must be South Carolina resident veteran'
    ],
    applicationProcess: 'Apply at South Carolina DMV with DD-214.',
    contactInfo: 'South Carolina DMV: (803) 896-5000',
    website: 'https://www.scdmvonline.com/',
    lastUpdated: 'Dec 2025'
  },

  // South Dakota Benefits
  {
    id: 'sd-housing-001',
    stateId: 'SD',
    stateName: 'South Dakota',
    title: 'Property Tax Relief',
    category: 'housing',
    description: 'Property tax reductions for disabled veterans.',
    eligibility: [
      '70% or higher service-connected disability',
      '100% P&T disabled: Enhanced relief'
    ],
    applicationProcess: 'Apply with county director of equalization.',
    contactInfo: 'Contact county equalization office',
    website: 'https://vetaffairs.sd.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax relief'
  },
  {
    id: 'sd-recreation-001',
    stateId: 'SD',
    stateName: 'South Dakota',
    title: 'Parks Access',
    category: 'recreation',
    description: 'Reduced hunting/fishing licenses and park access.',
    eligibility: [
      'Must be South Dakota resident veteran'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses.',
    contactInfo: 'South Dakota Game, Fish and Parks: (605) 773-3485',
    website: 'https://gfp.sd.gov/',
    lastUpdated: 'Nov 2025'
  },
  {
    id: 'sd-health-001',
    stateId: 'SD',
    stateName: 'South Dakota',
    title: 'South Dakota Veterans Home',
    category: 'healthcare',
    description: 'State veterans home providing skilled nursing care.',
    eligibility: [
      'Must be South Dakota resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact South Dakota Department of Veterans Affairs.',
    contactInfo: 'South Dakota DVA: (605) 773-3269',
    website: 'https://vetaffairs.sd.gov/',
    lastUpdated: 'Jan 2026'
  },

  // Tennessee Benefits
  {
    id: 'tn-edu-001',
    stateId: 'TN',
    stateName: 'Tennessee',
    title: 'Helping Heroes Grant',
    category: 'education',
    description: 'Tuition assistance for Tennessee veterans attending public institutions.',
    eligibility: [
      'Must be Tennessee resident',
      'Must have been honorably discharged',
      'Must have served after 9/11'
    ],
    applicationProcess: 'Apply through Tennessee Department of Veterans Services.',
    contactInfo: 'Tennessee Department of Veterans Services: (615) 741-2345',
    website: 'https://www.tn.gov/veteran/education.html',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'tn-housing-001',
    stateId: 'TN',
    stateName: 'Tennessee',
    title: 'Property Tax Relief',
    category: 'housing',
    description: 'Property tax relief for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax relief'
    ],
    applicationProcess: 'Apply with county trustee.',
    contactInfo: 'Contact county trustee',
    website: 'https://www.tn.gov/veteran/benefits.html',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive property tax relief'
  },
  {
    id: 'tn-employment-001',
    stateId: 'TN',
    stateName: 'Tennessee',
    title: 'Employment Preference',
    category: 'employment',
    description: 'State hiring preference for Tennessee veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Tennessee DVS: (615) 741-2345',
    website: 'https://www.tn.gov/veteran/employment.html',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'tn-recreation-001',
    stateId: 'TN',
    stateName: 'Tennessee',
    title: 'Hunting/Fishing Perks',
    category: 'recreation',
    description: 'Reduced hunting/fishing licenses for disabled veterans.',
    eligibility: [
      'Must be Tennessee resident veteran with service-connected disability'
    ],
    applicationProcess: 'Present disability rating when purchasing licenses.',
    contactInfo: 'Tennessee Wildlife Resources Agency: (615) 781-6500',
    website: 'https://www.tn.gov/twra/',
    lastUpdated: 'Nov 2025'
  },

  // Utah Benefits
  {
    id: 'ut-edu-001',
    stateId: 'UT',
    stateName: 'Utah',
    title: 'Utah State Tuition Waiver',
    category: 'education',
    description: 'Tuition waivers for Utah veterans at state institutions.',
    eligibility: [
      'Must be Utah resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through Utah State Board of Regents.',
    contactInfo: 'Utah Department of Veterans and Military Affairs: (801) 326-2372',
    website: 'https://veterans.utah.gov/education/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'ut-housing-001',
    stateId: 'UT',
    stateName: 'Utah',
    title: 'Property Tax Abatement',
    category: 'housing',
    description: 'Property tax abatement for disabled veterans.',
    eligibility: [
      '10% or higher service-connected disability',
      '100% P&T disabled: Enhanced abatement'
    ],
    applicationProcess: 'Apply with county auditor.',
    contactInfo: 'Contact county auditor',
    website: 'https://veterans.utah.gov/benefits/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax abatement'
  },
  {
    id: 'ut-recreation-001',
    stateId: 'UT',
    stateName: 'Utah',
    title: 'Outdoor Access',
    category: 'recreation',
    description: 'Reduced hunting/fishing licenses and state park access.',
    eligibility: [
      'Must be Utah resident veteran'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses.',
    contactInfo: 'Utah Division of Wildlife Resources: (801) 538-4700',
    website: 'https://wildlife.utah.gov/',
    lastUpdated: 'Nov 2025'
  },

  // Vermont Benefits
  {
    id: 'vt-housing-001',
    stateId: 'VT',
    stateName: 'Vermont',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with local listers or assessors.',
    contactInfo: 'Contact local listers/assessors',
    website: 'https://vtafm.vermont.gov/veterans-affairs/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'vt-recreation-001',
    stateId: 'VT',
    stateName: 'Vermont',
    title: 'Parks & Recreation Discounts',
    category: 'recreation',
    description: 'Discounted hunting/fishing licenses and park access.',
    eligibility: [
      'Must be Vermont resident veteran'
    ],
    applicationProcess: 'Present DD-214 when purchasing licenses.',
    contactInfo: 'Vermont Fish and Wildlife: (802) 828-1000',
    website: 'https://vtfishandwildlife.com/',
    lastUpdated: 'Nov 2025'
  },
  {
    id: 'vt-health-001',
    stateId: 'VT',
    stateName: 'Vermont',
    title: 'Vermont Veterans Home',
    category: 'healthcare',
    description: 'State veterans home providing skilled nursing care.',
    eligibility: [
      'Must be Vermont resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Vermont Office of Veterans Affairs.',
    contactInfo: 'Vermont Office of Veterans Affairs: (802) 828-3379',
    website: 'https://vtafm.vermont.gov/veterans-affairs',
    lastUpdated: 'Jan 2026'
  },

  // Virginia Benefits
  {
    id: 'va-edu-001',
    stateId: 'VA',
    stateName: 'Virginia',
    title: 'Virginia Military Survivors & Dependents Education Program',
    category: 'education',
    description: 'Free tuition for dependents of disabled or deceased veterans.',
    eligibility: [
      'Parent must be Virginia veteran',
      'Parent must be 90% or higher service-connected disabled or killed in action'
    ],
    applicationProcess: 'Apply through Virginia Department of Veterans Services.',
    contactInfo: 'Virginia Department of Veterans Services: (804) 786-0286',
    website: 'https://www.dvs.virginia.gov/education',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive free tuition at Virginia public schools.',
    ptBenefits: '100% P&T veterans: Dependents receive free tuition at Virginia state institutions'
  },
  {
    id: 'va-housing-001',
    stateId: 'VA',
    stateName: 'Virginia',
    title: 'Property Tax Relief',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Property tax exemption'
    ],
    applicationProcess: 'Apply with commissioner of revenue.',
    contactInfo: 'Contact local commissioner of revenue',
    website: 'https://www.dvs.virginia.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'va-employment-001',
    stateId: 'VA',
    stateName: 'Virginia',
    title: 'Employment Preference',
    category: 'employment',
    description: 'State hiring preference for Virginia veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Virginia DVS: (804) 786-0286',
    website: 'https://www.dvs.virginia.gov/employment',
    lastUpdated: 'Dec 2025'
  },

  // Washington Benefits
  {
    id: 'wa-edu-001',
    stateId: 'WA',
    stateName: 'Washington',
    title: 'Washington State Veterans Education Benefits',
    category: 'education',
    description: 'Tuition waivers for Washington veterans and dependents.',
    eligibility: [
      'Must be Washington resident',
      'Must be honorably discharged veteran or qualifying dependent'
    ],
    applicationProcess: 'Apply through Washington Department of Veterans Affairs.',
    contactInfo: 'Washington Department of Veterans Affairs: (360) 725-2169',
    website: 'https://www.dva.wa.gov/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'wa-housing-001',
    stateId: 'WA',
    stateName: 'Washington',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '30% or higher service-connected disability',
      '100% P&T disabled: Full exemption'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://www.dva.wa.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption'
  },
  {
    id: 'wa-recreation-001',
    stateId: 'WA',
    stateName: 'Washington',
    title: 'Recreation Access',
    category: 'recreation',
    description: 'Discounted hunting/fishing licenses and park access for disabled veterans.',
    eligibility: [
      'Must be Washington resident veteran with service-connected disability'
    ],
    applicationProcess: 'Present disability rating when purchasing licenses.',
    contactInfo: 'Washington Department of Fish and Wildlife: (360) 902-2200',
    website: 'https://wdfw.wa.gov/',
    lastUpdated: 'Nov 2025'
  },

  // West Virginia Benefits
  {
    id: 'wv-edu-001',
    stateId: 'WV',
    stateName: 'West Virginia',
    title: 'Veterans Education Benefits',
    category: 'education',
    description: 'Tuition waivers for West Virginia veterans at state institutions.',
    eligibility: [
      'Must be West Virginia resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Apply through West Virginia Division of Veterans Affairs.',
    contactInfo: 'West Virginia Division of Veterans Affairs: (304) 558-3661',
    website: 'https://veterans.wv.gov/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'wv-housing-001',
    stateId: 'WV',
    stateName: 'West Virginia',
    title: 'Property Tax Exemptions',
    category: 'housing',
    description: 'Property tax exemptions for disabled veterans.',
    eligibility: [
      '50% or higher service-connected disability',
      '100% P&T disabled: Enhanced exemption'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://veterans.wv.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax exemption'
  },
  {
    id: 'wv-employment-001',
    stateId: 'WV',
    stateName: 'West Virginia',
    title: 'Employment Programs',
    category: 'employment',
    description: 'State hiring preference and job assistance for West Virginia veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact West Virginia Veterans Affairs.',
    contactInfo: 'West Virginia DVA: (304) 558-3661',
    website: 'https://veterans.wv.gov/employment',
    lastUpdated: 'Dec 2025'
  },

  // Wisconsin Benefits
  {
    id: 'wi-edu-001',
    stateId: 'WI',
    stateName: 'Wisconsin',
    title: 'Wisconsin GI Bill',
    category: 'education',
    description: 'Tuition remission for Wisconsin veterans and dependents.',
    eligibility: [
      'Must be Wisconsin resident',
      'Must be honorably discharged veteran or qualifying dependent',
      'Dependents of 100% P&T disabled veterans qualify'
    ],
    applicationProcess: 'Apply through Wisconsin Department of Veterans Affairs.',
    contactInfo: 'Wisconsin Department of Veterans Affairs: (608) 266-1311',
    website: 'https://dva.wi.gov/Pages/benefitsClaims/education.aspx',
    lastUpdated: 'Jan 2026',
    dependentEligibility: 'Dependents of 100% P&T veterans receive tuition remission.',
    ptBenefits: '100% P&T veterans and dependents receive tuition remission at Wisconsin public schools'
  },
  {
    id: 'wi-housing-001',
    stateId: 'WI',
    stateName: 'Wisconsin',
    title: 'Property Tax Credit',
    category: 'housing',
    description: 'Property tax credits for disabled veterans.',
    eligibility: [
      '70% or higher service-connected disability',
      '100% P&T disabled: Enhanced credit'
    ],
    applicationProcess: 'Apply through Wisconsin Department of Revenue.',
    contactInfo: 'Wisconsin DOR: (608) 266-2772',
    website: 'https://www.revenue.wi.gov/',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive maximum property tax credit'
  },
  {
    id: 'wi-dmv-001',
    stateId: 'WI',
    stateName: 'Wisconsin',
    title: 'Veteran License Plates',
    category: 'other',
    description: 'Veteran license plates and disabled veteran parking.',
    eligibility: [
      'Must be Wisconsin resident veteran'
    ],
    applicationProcess: 'Apply at Wisconsin DMV with DD-214.',
    contactInfo: 'Wisconsin DMV: (608) 266-2353',
    website: 'https://wisconsindot.gov/Pages/dmv/',
    lastUpdated: 'Dec 2025'
  },
  {
    id: 'wi-recreation-001',
    stateId: 'WI',
    stateName: 'Wisconsin',
    title: 'Outdoor Discounts',
    category: 'recreation',
    description: 'Discounted hunting/fishing licenses for disabled veterans.',
    eligibility: [
      'Must be Wisconsin resident veteran with service-connected disability'
    ],
    applicationProcess: 'Present disability rating when purchasing licenses.',
    contactInfo: 'Wisconsin DNR: (608) 266-2621',
    website: 'https://dnr.wisconsin.gov/',
    lastUpdated: 'Nov 2025'
  },

  // Wyoming Benefits
  {
    id: 'wy-edu-001',
    stateId: 'WY',
    stateName: 'Wyoming',
    title: 'Free Tuition for Veterans & Dependents',
    category: 'education',
    description: 'Free tuition at Wyoming community colleges for veterans and dependents of disabled/deceased veterans.',
    eligibility: [
      'Must be Wyoming resident',
      'Must be honorably discharged veteran or qualifying dependent'
    ],
    applicationProcess: 'Apply through Wyoming Veterans Commission.',
    contactInfo: 'Wyoming Veterans Commission: (307) 777-8151',
    website: 'https://veterans.wyo.gov/education',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'wy-housing-001',
    stateId: 'WY',
    stateName: 'Wyoming',
    title: 'Full Tax Exemptions',
    category: 'housing',
    description: 'Full tax exemptions on income, retirement pay, and property for disabled veterans.',
    eligibility: [
      '100% service-connected disabled: Full property tax exemption',
      'No state income tax on military retirement or VA disability pay'
    ],
    applicationProcess: 'Apply with county assessor.',
    contactInfo: 'Contact county assessor',
    website: 'https://veterans.wyo.gov/benefits',
    lastUpdated: 'Dec 2025',
    ptBenefits: '100% P&T disabled veterans receive full property tax exemption and no state income tax'
  },
  {
    id: 'wy-employment-001',
    stateId: 'WY',
    stateName: 'Wyoming',
    title: 'Employment Preference',
    category: 'employment',
    description: 'State hiring preference for Wyoming veterans.',
    eligibility: [
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Indicate veteran status on state applications.',
    contactInfo: 'Wyoming Veterans Commission: (307) 777-8151',
    website: 'https://veterans.wyo.gov/employment',
    lastUpdated: 'Dec 2025'
  },

  // U.S. Territories

  // Puerto Rico Benefits
  {
    id: 'pr-edu-001',
    stateId: 'PR',
    stateName: 'Puerto Rico',
    title: 'Education Benefits',
    category: 'education',
    description: 'Local education assistance and tuition programs for Puerto Rico veterans.',
    eligibility: [
      'Must be Puerto Rico resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Puerto Rico Veterans Affairs Office.',
    contactInfo: 'Puerto Rico Veterans Affairs: (787) 758-5760',
    website: 'https://www.veteransaffairs.pr.gov/',
    lastUpdated: 'Jan 2026'
  },
  {
    id: 'pr-housing-001',
    stateId: 'PR',
    stateName: 'Puerto Rico',
    title: 'Tax & DMV Perks',
    category: 'housing',
    description: 'Local tax benefits and DMV perks for Puerto Rico veterans.',
    eligibility: [
      'Must be Puerto Rico resident veteran'
    ],
    applicationProcess: 'Contact local government offices.',
    contactInfo: 'Contact local municipality',
    website: 'https://www.veteransaffairs.pr.gov/',
    lastUpdated: 'Dec 2025'
  },

  // Guam Benefits
  {
    id: 'gu-edu-001',
    stateId: 'GU',
    stateName: 'Guam',
    title: 'Veterans Education Support',
    category: 'education',
    description: 'Local education benefits for Guam veterans.',
    eligibility: [
      'Must be Guam resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact Guam Veterans Affairs Office.',
    contactInfo: 'Guam Veterans Affairs Office: (671) 475-8388',
    website: 'https://gvao.guam.gov/',
    lastUpdated: 'Jan 2026'
  },

  // U.S. Virgin Islands Benefits
  {
    id: 'vi-edu-001',
    stateId: 'VI',
    stateName: 'U.S. Virgin Islands',
    title: 'Local Veterans Programs',
    category: 'education',
    description: 'Veterans benefits vary by local government administration.',
    eligibility: [
      'Must be U.S. Virgin Islands resident',
      'Must be honorably discharged veteran'
    ],
    applicationProcess: 'Contact local government offices.',
    contactInfo: 'Contact local Veterans Affairs office',
    website: 'https://www.vi.gov/',
    lastUpdated: 'Jan 2026'
  }
];

export const getStateName = (stateId: string): string => {
  const stateNames: { [key: string]: string } = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };
  return stateNames[stateId] || stateId;
};

export const getBenefitsByState = (stateId: string): StateBenefit[] => {
  return stateBenefitsData.filter(benefit => benefit.stateId === stateId);
};

export const getBenefitsByCategory = (category: string): StateBenefit[] => {
  return stateBenefitsData.filter(benefit => benefit.category === category);
};

export const getCategoryIcon = (category: string): string => {
  const iconMap: { [key: string]: string } = {
    'education': 'AcademicCapIcon',
    'healthcare': 'HeartIcon',
    'housing': 'HomeIcon',
    'financial': 'BanknotesIcon',
    'recreation': 'TrophyIcon',
    'employment': 'BriefcaseIcon',
    'burial': 'CasketIcon',
    'other': 'SparklesIcon'
  };
  return iconMap[category] || 'DocumentTextIcon';
};

export const getCategoryColor = (category: string): string => {
  const colorMap: { [key: string]: string } = {
    'education': 'bg-blue-500/10 text-blue-600 border-blue-200',
    'healthcare': 'bg-red-500/10 text-red-600 border-red-200',
    'housing': 'bg-green-500/10 text-green-600 border-green-200',
    'financial': 'bg-yellow-500/10 text-yellow-700 border-yellow-200',
    'recreation': 'bg-purple-500/10 text-purple-600 border-purple-200',
    'employment': 'bg-indigo-500/10 text-indigo-600 border-indigo-200',
    'burial': 'bg-gray-500/10 text-gray-600 border-gray-200',
    'other': 'bg-gray-500/10 text-gray-600 border-gray-200'
  };
  return colorMap[category] || 'bg-gray-500/10 text-gray-600 border-gray-200';
};